import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useToggle } from 'react-use';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useGetSectionRowScoreRecommendations } from 'src/hooks/api/useGetSectionRowScoreRecommendations';
import {
  Event,
  Listing,
  SectionRowScoreRecommendations,
  SelectedSectionSettings,
  VenueMapInfo,
} from 'src/WebApiController';

export const useListingScoreRecommendations = (
  event: Event | undefined,
  venueMapInfo?: VenueMapInfo | null,
  selectedListing?: Listing | undefined,
  disabled?: boolean
) => {
  const { selectedSectionRecommendations } =
    useGetSectionRowScoreRecommendations(
      event?.viagId ?? undefined,
      venueMapInfo?.venueCfgId,
      selectedListing?.rowId ?? undefined,
      disabled ||
        !isEmpty(selectedListing?.compSectionSettings?.sectionIdFilter)
    );

  const recommendedSelectedSectionSettings = useMemo(() => {
    if (disabled) {
      return undefined;
    }
    if (
      !selectedListing?.rowId ||
      !selectedSectionRecommendations?.rowRecommendations
    ) {
      return undefined;
    }

    return selectedSectionRecommendations.rowRecommendations[
      selectedListing.rowId
    ];
  }, [
    disabled,
    selectedListing?.rowId,
    selectedSectionRecommendations?.rowRecommendations,
  ]);

  const sectionRowScoreRecommendations = useMemo(():
    | (SectionRowScoreRecommendations & { listingId: number })
    | undefined => {
    if (
      !(
        selectedListing?.id &&
        event?.viagId &&
        venueMapInfo?.venueCfgId &&
        selectedSectionRecommendations?.modelVersionId &&
        recommendedSelectedSectionSettings
      )
    ) {
      return undefined;
    }

    return {
      listingId: selectedListing?.id,
      eventId: event.viagId,
      venueConfigId: venueMapInfo.venueCfgId,
      modelVersionId: selectedSectionRecommendations.modelVersionId,
      recommendedSelectedSectionSettings: recommendedSelectedSectionSettings,
    };
  }, [
    event?.viagId,
    recommendedSelectedSectionSettings,
    selectedListing?.id,
    selectedSectionRecommendations?.modelVersionId,
    venueMapInfo?.venueCfgId,
  ]);

  return {
    sectionRowScoreRecommendations,
  };
};

export const getMergedSelectedSectionSettings = (
  compListingSelectedSectionSettings?: SelectedSectionSettings | null,
  recommendedSelectedSectionSettings?: SelectedSectionSettings | null,
  force = false
) => {
  if (!recommendedSelectedSectionSettings) {
    return compListingSelectedSectionSettings;
  }
  if (
    !force &&
    compListingSelectedSectionSettings?.sectionIdFilter &&
    compListingSelectedSectionSettings?.sectionIdFilter.length > 0
  ) {
    return compListingSelectedSectionSettings;
  }
  const {
    sectionIdFilter,
    sectionRowIdFilter,
    rowIdFilter,
    rowOffset,
    ...rest
  } = compListingSelectedSectionSettings ?? ({} as SelectedSectionSettings);
  return {
    ...rest,
    rowOffset,
    sectionIdFilter: Array.from(
      new Set([
        ...(sectionIdFilter ?? []),
        ...(recommendedSelectedSectionSettings.sectionIdFilter ?? []),
      ])
    ),
    sectionRowIdFilter:
      rowOffset != null
        ? null
        : {
            ...(sectionRowIdFilter ?? {}),
            ...(recommendedSelectedSectionSettings.sectionRowIdFilter ?? {}),
          },
    rowIdFilter:
      rowOffset != null
        ? null
        : Array.from(
            new Set([
              ...(rowIdFilter ?? []),
              ...(recommendedSelectedSectionSettings.rowIdFilter ?? []),
            ])
          ),
  };
};

export const useOnePagerContainerViewMode = () => {
  const [forceToVerticalView, setForceToVerticalView] = useToggle(false);
  const [dynamicVerticalView, setDynamicVerticalView] = useToggle(false);
  const onePagerContainerRef = useRef<HTMLDivElement>(null);
  const { isOnSidePanel } = useEventHubContext();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const checkAndUpdateViewMode = useCallback(
    (width: number) => {
      const shouldUserVerticalView = !isOnSidePanel && width < 815;
      if (shouldUserVerticalView !== dynamicVerticalView) {
        setDynamicVerticalView(shouldUserVerticalView);
      }
    },
    [dynamicVerticalView, isOnSidePanel, setDynamicVerticalView]
  );

  const onOnePagerContainerRefInitialized = useCallback(
    (node: HTMLDivElement | null) => {
      // Clean up previous observer if it exists
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (node) {
        const width = node.getBoundingClientRect().width;
        checkAndUpdateViewMode(width);

        // Set up ResizeObserver
        resizeObserverRef.current = new ResizeObserver((entries) => {
          for (const entry of entries) {
            const width = entry.contentRect.width;
            checkAndUpdateViewMode(width);
          }
        });
        resizeObserverRef.current.observe(node);
      }
    },
    [checkAndUpdateViewMode]
  );

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  const useVerticalView = useMemo(
    () => forceToVerticalView || dynamicVerticalView,
    [dynamicVerticalView, forceToVerticalView]
  );

  return {
    useVerticalView,
    setForceToVerticalView,
    onePagerContainerRef,
    onOnePagerContainerRefInitialized,
  };
};
